import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getCookie } from 'cookies-next';
import type { RequestDocument } from 'graphql-request';
import { request } from 'graphql-request';

import { FEATURE_FLAG_NAMES } from '@/entities/feature-flags/constants';
import { useFeatureFlags } from '@/entities/feature-flags/hooks/useFeatureFlags';
import { refreshUserSpecificCarouselsAfterAuthChange } from '@/entities/hubs/utils/carousels/refreshUserSpecificCarouselsAfterAuthChange';
import { useGetConfigByKey } from '@/entities/regional-configuration/hooks/useGetConfigByKey';
import { graphql } from '@/graphql/__generated/gql';
import type { FullUser } from '@/graphql/__generated/gql/graphql';
import { runPostSignOutClientSetup } from '@/hooks/signout/runPostSignOutClientSetup';
import { appLogger } from '@/utils/app-logger/appLogger';
import { AUTH_COOKIE_KEY } from '@/utils/constants/common';
import { invalidateMyListAfterAuthChange } from '@/utils/mylist/invalidateMyListAfterAuthChange';
import { type PlatformRestartType, ZONE_APP_SHELL_GRAPHQL_URL } from '@/utils/platformRestart';
import { resetSentimentAfterAuthChange } from '@/utils/sentiment/resetSentimentAfterAuthChange';

export const signOutFlowMutationKey = 'signOutFlowMutationKey';

export const logOutMutation = graphql(/* GraphQL */ `
    mutation LogOut($token: String!) {
        logOut(token: $token) {
            success
            message
            isLoggedIn
            errors
        }
    }
`);

async function runLogOut(errors: string[]): Promise<void> {
    const token = getCookie(AUTH_COOKIE_KEY);
    const tokenStr = typeof token === 'string' ? token : '';

    const logOutResult = (await request(
        ZONE_APP_SHELL_GRAPHQL_URL,
        logOutMutation as RequestDocument,
        {
            token: tokenStr,
        },
    )) as { logOut: { success: boolean; errors?: string | null; message?: string | null } };

    if (!logOutResult.logOut?.success) {
        const msg = logOutResult.logOut?.errors ?? logOutResult.logOut?.message ?? 'Log out failed';
        errors.push(`logOut: ${msg}`);
        throw new Error(errors.join('; '));
    }
}

async function runSignOutFlow(
    platformRestartEnabled: boolean,
    queryClient: QueryClient,
): Promise<PlatformRestartType | undefined> {
    const errors: string[] = [];
    await runLogOut(errors);
    return runPostSignOutClientSetup({ queryClient, platformRestartEnabled, errors });
}

async function refreshMyListCaches(queryClient: QueryClient): Promise<void> {
    try {
        await invalidateMyListAfterAuthChange(queryClient);
    } catch (error) {
        appLogger.error(
            'My-list cache refresh failed after sign-out',
            {
                component: 'useSignOutFlow',
            },
            error,
        );
    }
}

export type UseSignOutFlowOptions = {
    onDone?: (fullUser: FullUser | undefined) => Promise<void> | void;
};

/**
 * @description This hook is used to sign out the user from the account. It will make two distinct calls to first
 * invalidate the CBS_COM token via the logout api then restart the platform session via the platform restart api.
 * Upon successful sign out, the full user profile cache is refreshed before my-list
 * cache refresh and the onDone callback.
 *
 * @documentation https://paramount.atlassian.net/wiki/spaces/STC/pages/448637086/Converged+Account+Login#Log-Out
 * @param options {UseSignOutFlowOptions}
 * @param options.onDone {Function} - Callback function to be called when the sign out flow is complete.
 * @returns {Object|undefined} - Object containing the sign out click handler, error, isLoading, and onDone callback.
 */
export const useSignOutFlow = (options?: UseSignOutFlowOptions) => {
    const { onDone } = options ?? {};
    const queryClient = useQueryClient();
    const { getFeatureFlag } = useFeatureFlags();
    const isKetchEnabled = getFeatureFlag(FEATURE_FLAG_NAMES.KETCH_ENABLED);
    const platformRestartEnabled =
        useGetConfigByKey<boolean>('options.platformRestartEnabled') ?? false;

    const { mutate, error, isPending } = useMutation<PlatformRestartType | undefined, Error, void>({
        mutationFn: () => runSignOutFlow(platformRestartEnabled, queryClient),
        mutationKey: [signOutFlowMutationKey],
        gcTime: 0,
        onSuccess: async () => {
            // Call syncProfile to clear paramount_privacy_id if ketch is enabled
            if (isKetchEnabled && typeof window !== 'undefined' && window.cbsoptanon?.cmd) {
                window.cbsoptanon.cmd.push((cmp) => {
                    cmp?.ot?.syncProfile?.('', '');
                });
            }
            await Promise.all([
                refreshMyListCaches(queryClient),
                refreshUserSpecificCarouselsAfterAuthChange(queryClient),
                resetSentimentAfterAuthChange(queryClient),
            ]);
            onDone?.(undefined);
        },
    });

    return {
        signOutClickHandler: mutate,
        error: error ?? null,
        isLoading: isPending,
        onDone,
    };
};
